import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AppState } from '../app.reducer';
import { Store } from '@ngrx/store';
import { BoardUsersService } from 'src/app/services/board-users.service';
import { TranslateService } from '@ngx-translate/core';
import * as DeviceBoardusersActions from './device-boardusers.actions';
import { catchError, map, of, switchMap, tap, withLatestFrom } from 'rxjs';
import { selectCurrentTechnician, selectUser } from '../user/user.selector';
import { selectCurrentDevice } from '../device/device.selector';
import { Notification, NotificationLevel } from 'src/app/model/notification';
import * as NotificationsActions from '../notifications/notifications.actions';
import { BoardUsersExportList } from 'src/app/model/board-user';
import { PaginationParams } from 'src/app/model/api/pagination-params';
import { BoardUsersListsRespData } from 'src/app/model/api/data-rest';
import { BOARD_USERS_LISTS_PAGE_DEFAULT_SIZE, BOARD_USERS_LISTS_DEFAULT_SORT_COLUMN, BOARD_USERS_LISTS_DEFAULT_SORT_COLUMN_DIRECTION } from 'src/app/conf/board-users';

@Injectable()
export class DeviceBoardusersEffects {

	getBoardusersLists$ = createEffect(() => this.actions$.pipe(
		ofType(DeviceBoardusersActions.getBoardusersLists),
		withLatestFrom(
			this.store.select(selectUser),
			this.store.select(selectCurrentTechnician),
			this.store.select(selectCurrentDevice)
		),
		switchMap(([action, currentUser, currentTechnician, currentDevice]) => {
			let userId = currentUser.Id;
			if (currentTechnician) {
				userId = currentTechnician.PersonifiedId;
			}
			const modelId = currentDevice.ModelId;
			let paging: PaginationParams = {
				size: BOARD_USERS_LISTS_PAGE_DEFAULT_SIZE,
				page: 1,
				sort: BOARD_USERS_LISTS_DEFAULT_SORT_COLUMN,
				order: BOARD_USERS_LISTS_DEFAULT_SORT_COLUMN_DIRECTION,
			}
			if (action.paging) {
				paging = action.paging;
			}
			return this.boardUsersService.getBoardusersLists(userId, modelId, paging).pipe(
				map((resp: BoardUsersListsRespData) => {
					if (resp.Success) {
						return DeviceBoardusersActions.getBoardusersListsSuccess({
							boardUsersResp: resp
						});
					} else {
						return DeviceBoardusersActions.getBoardusersListsFailed({ errorMessage: resp.ErrorMessage });
					}
				}),
				catchError((error) => {
					return of(DeviceBoardusersActions.getBoardusersListsFailed({ errorMessage: error.message }));
				})
			)
		})
	));

	exportBoarduserList$ = createEffect(() => this.actions$.pipe(
		ofType(DeviceBoardusersActions.exportBoarduserList),
		withLatestFrom(
			this.store.select(selectUser),
			this.store.select(selectCurrentTechnician),
			this.store.select(selectCurrentDevice),
		),
		switchMap(([action, currentUser, currentTechnician, device]) => {
			let userId = currentUser.Id;
			if (currentTechnician) {
				userId = currentTechnician.PersonifiedId;
			}
			const boardUsersExportList: BoardUsersExportList = {
				Name: action.name,
				DeviceId: device.Id,
				Version: action.version,
			}
			return this.boardUsersService.exportBoardUsersList(userId, boardUsersExportList).pipe(
				map(resp => {
					if (resp.Success) {
						return DeviceBoardusersActions.exportBoarduserListSuccess({ respData: resp.Data, message: this.translate.instant('LIST_EXPORTED') });
					} else {
						if (resp.ErrorCode === 55) { // TRANSLATE ERROR: timeout acquire of new user
							return DeviceBoardusersActions.exportBoarduserListFailed({ errorMessage: this.translate.instant('SYNC_ERROR_RUNNING_TASK') });
						} else {
							return DeviceBoardusersActions.exportBoarduserListFailed({ errorMessage: resp.ErrorMessage });
						}
					}
				}),
				catchError((error) => {
					console.error(error);
					return of(DeviceBoardusersActions.exportBoarduserListFailed({ errorMessage: error.error.ErrorMessage }));
				})
			)
		})
	));

	editBoarduserList$ = createEffect(() => this.actions$.pipe(
		ofType(DeviceBoardusersActions.editBoarduserList),
		withLatestFrom(
			this.store.select(selectUser),
			this.store.select(selectCurrentTechnician),
		),
		switchMap(([action, currentUser, currentTechnician]) => {
			let userId = currentUser.Id;
			if (currentTechnician) {
				userId = currentTechnician.PersonifiedId;
			}
			const boardUsersEditList: BoardUsersExportList = {
				Name: action.name,
				Version: action.version,
			}
			return this.boardUsersService.editBoardUsersList(userId, boardUsersEditList, action.listId).pipe(
				map(resp => {
					if (resp.Success) {
						return DeviceBoardusersActions.editBoarduserListSuccess({ respData: resp.Data, message: this.translate.instant('LIST_UPDATED_SUCCESS'), paging: action.paging });
					} else {
						return DeviceBoardusersActions.editBoarduserListFailed({ errorMessage: resp.ErrorMessage });
					}
				}),
				catchError((error) => {
					console.error(error);
					return of(DeviceBoardusersActions.editBoarduserListFailed({ errorMessage: error.error.ErrorMessage }));
				})
			)
		})
	));

	editBoarduserListSuccess$ = createEffect(() => this.actions$.pipe(
		ofType(DeviceBoardusersActions.editBoarduserListSuccess),
		tap((action) => {
			const paging = action.paging;
			this.store.dispatch(DeviceBoardusersActions.getBoardusersLists({
				paging: {
					size: paging ? paging.size : BOARD_USERS_LISTS_PAGE_DEFAULT_SIZE,
					page: paging ? paging.page : 1,
					sort: paging ? paging.sort : BOARD_USERS_LISTS_DEFAULT_SORT_COLUMN,
					order: paging ? paging.order : BOARD_USERS_LISTS_DEFAULT_SORT_COLUMN_DIRECTION,
				}
			}));
		}),
	), { dispatch: false });

	deleteBoarduserList$ = createEffect(() => this.actions$.pipe(
		ofType(DeviceBoardusersActions.deleteBoarduserList),
		withLatestFrom(
			this.store.select(selectUser),
			this.store.select(selectCurrentTechnician),
		),
		switchMap(([action, currentUser, currentTechnician]) => {
			let userId = currentUser.Id;
			if (currentTechnician) {
				userId = currentTechnician.PersonifiedId;
			}
			return this.boardUsersService.deleteBoarduserList(userId, action.listId, action.version).pipe(
				map(resp => {
					if (resp.Success) {
						return DeviceBoardusersActions.deleteBoarduserListSuccess({ respData: resp.Data, message: this.translate.instant('LIST_UPDATED_SUCCESS'), paging: action.paging });
					} else {
						return DeviceBoardusersActions.deleteBoarduserListFailed({ errorMessage: resp.ErrorMessage });
					}
				}),
				catchError((error) => {
					console.error(error);
					return of(DeviceBoardusersActions.deleteBoarduserListFailed({ errorMessage: error.error.ErrorMessage }));
				})
			)
		})
	));

	deleteBoarduserListSuccess$ = createEffect(() => this.actions$.pipe(
		ofType(DeviceBoardusersActions.deleteBoarduserListSuccess),
		tap((action) => {
			const paging = action.paging;
			this.store.dispatch(DeviceBoardusersActions.getBoardusersLists({
				paging: {
					size: paging ? paging.size : BOARD_USERS_LISTS_PAGE_DEFAULT_SIZE,
					page: paging ? paging.page : 1,
					sort: paging ? paging.sort : BOARD_USERS_LISTS_DEFAULT_SORT_COLUMN,
					order: paging ? paging.order : BOARD_USERS_LISTS_DEFAULT_SORT_COLUMN_DIRECTION,
				}
			}));
		}),
	), { dispatch: false });

	importBoarduserList$ = createEffect(() => this.actions$.pipe(
		ofType(DeviceBoardusersActions.importBoarduserList),
		withLatestFrom(
			this.store.select(selectUser),
			this.store.select(selectCurrentTechnician),
			this.store.select(selectCurrentDevice),
		),
		switchMap(([action, currentUser, currentTechnician, device]) => {
			let userId = currentUser.Id;
			if (currentTechnician) {
				userId = currentTechnician.PersonifiedId;
			}
			return this.boardUsersService.importBoardUsersList(userId, device.Id, action.listId).pipe(
				map(resp => {
					if (resp.Success) {
						return DeviceBoardusersActions.importBoarduserListSuccess({ respData: resp.Data, message: this.translate.instant('LIST_IMPORTED'), paging: action.paging });
					} else {
						if (resp.ErrorCode === 55) { // TRANSLATE ERROR: timeout acquire of new user
							return DeviceBoardusersActions.importBoarduserListFailed({ errorMessage: this.translate.instant('SYNC_ERROR_RUNNING_TASK') });
						} else {
							return DeviceBoardusersActions.importBoarduserListFailed({ errorMessage: resp.ErrorMessage });
						}
					}
				}),
				catchError((error) => {
					console.error(error);
					return of(DeviceBoardusersActions.importBoarduserListFailed({ errorMessage: error.error.ErrorMessage }));
				})
			)
		})
	));

	importBoarduserListSuccess$ = createEffect(() => this.actions$.pipe(
		ofType(DeviceBoardusersActions.importBoarduserListSuccess),
		tap((action) => {
			const paging = action.paging;
			this.store.dispatch(DeviceBoardusersActions.getBoardusersLists({
				paging: {
					size: paging ? paging.size : BOARD_USERS_LISTS_PAGE_DEFAULT_SIZE,
					page: paging ? paging.page : 1,
					sort: paging ? paging.sort : BOARD_USERS_LISTS_DEFAULT_SORT_COLUMN,
					order: paging ? paging.order : BOARD_USERS_LISTS_DEFAULT_SORT_COLUMN_DIRECTION,
				}
			}));
		}),
	), { dispatch: false });

	notifySuccess$ = createEffect(() => this.actions$.pipe(
		ofType(
			DeviceBoardusersActions.exportBoarduserListSuccess,
			DeviceBoardusersActions.editBoarduserListSuccess,
			DeviceBoardusersActions.deleteBoarduserListSuccess,
			DeviceBoardusersActions.importBoarduserListSuccess,
		),
		tap((action) => {
			if (action.message === '') {
				return;
			}
			const notification = new Notification("BOARD-USERS", action.message, NotificationLevel.Success, '');
			this.store.dispatch(NotificationsActions.addNotification({ notification: notification, toastOnly: true }));
		})
	), { dispatch: false });

	notifyError$ = createEffect(() => this.actions$.pipe(
		ofType(
			DeviceBoardusersActions.getBoardusersListsFailed,
			DeviceBoardusersActions.exportBoarduserListFailed,
			DeviceBoardusersActions.editBoarduserListFailed,
			DeviceBoardusersActions.deleteBoarduserListFailed,
			DeviceBoardusersActions.importBoarduserListFailed,
		),
		tap((action) => {
			if (action.errorMessage === '') {
				return;
			}
			const notification = new Notification("BOARD-USERS", action.errorMessage, NotificationLevel.Error, '');
			this.store.dispatch(NotificationsActions.addNotification({ notification: notification, toastOnly: true }));
		})
	), { dispatch: false });

	constructor(
		private actions$: Actions,
		private store: Store<AppState>,
		private boardUsersService: BoardUsersService,
		private translate: TranslateService,
	) {
	}
}
